/**
 * ExecutiveSummaryPanel — Template-based executive insight bullets
 *
 * Generates 2–4 deterministic, rule-based insight bullets per tab.
 * No AI — pure template logic. Respects sufficiency tiers.
 */
import React from 'react';
import { FiTrendingUp, FiTrendingDown, FiMinus, FiAlertCircle } from 'react-icons/fi';
import type {
  FinancialHealthSection,
  OverviewSection,
} from '../../../types/insightsTypes';
import { SUFFICIENCY_THRESHOLDS } from '../../../types/insightsTypes';

// ── Insight bullet types ────────────────────────────────────────────────────

interface InsightBullet {
  icon: React.ReactNode;
  text: string;
  sentiment: 'positive' | 'negative' | 'neutral' | 'warning';
}

const sentimentColors: Record<string, string> = {
  positive: 'text-green-700 bg-green-50 border-green-200',
  negative: 'text-red-700 bg-red-50 border-red-200',
  neutral: 'text-slate-700 bg-slate-50 border-slate-200',
  warning: 'text-amber-700 bg-amber-50 border-amber-200',
};

// ── Template generators ─────────────────────────────────────────────────────

function generateOverviewInsights(ov: OverviewSection): InsightBullet[] {
  const bullets: InsightBullet[] = [];
  const n = ov.total_members.sample_size;

  if (n < SUFFICIENCY_THRESHOLDS.STANDARD_MIN) {
    bullets.push({
      icon: <FiAlertCircle size={14} />,
      text: `Sample size is ${n} member${n !== 1 ? 's' : ''} — insights are directional only. Minimum ${SUFFICIENCY_THRESHOLDS.STANDARD_MIN} recommended.`,
      sentiment: 'warning',
    });
    return bullets;
  }

  // Total members
  if (ov.total_members.value != null) {
    bullets.push({
      icon: <FiMinus size={14} />,
      text: `Portfolio includes ${ov.total_members.value.toLocaleString()} active member${ov.total_members.value !== 1 ? 's' : ''} with VeraScore data.`,
      sentiment: 'neutral',
    });
  }

  // Avg score
  if (ov.avg_verascore.value != null) {
    const score = Math.round(ov.avg_verascore.value * 10) / 10;
    const isStrong = score >= 60;
    bullets.push({
      icon: isStrong ? <FiTrendingUp size={14} /> : <FiTrendingDown size={14} />,
      text: `Average VeraScore is ${score}${isStrong ? ' — portfolio health is stable' : ' — portfolio may need attention'}.`,
      sentiment: isStrong ? 'positive' : 'negative',
    });
  }

  // Growth trend
  if (ov.member_growth_count.data.length >= 2) {
    const latest = ov.member_growth_count.data[ov.member_growth_count.data.length - 1].value;
    const prior = ov.member_growth_count.data[ov.member_growth_count.data.length - 2].value;
    const delta = latest - prior;
    if (delta > 0) {
      bullets.push({
        icon: <FiTrendingUp size={14} />,
        text: `Member count grew by ${delta} in the latest period.`,
        sentiment: 'positive',
      });
    } else if (delta < 0) {
      bullets.push({
        icon: <FiTrendingDown size={14} />,
        text: `Member count declined by ${Math.abs(delta)} in the latest period.`,
        sentiment: 'negative',
      });
    }
  }

  return bullets.slice(0, 4);
}

function generateFinancialHealthInsights(fh: FinancialHealthSection): InsightBullet[] {
  const bullets: InsightBullet[] = [];
  const n = fh.avg_verascore.sample_size;

  if (n < SUFFICIENCY_THRESHOLDS.STANDARD_MIN) {
    bullets.push({
      icon: <FiAlertCircle size={14} />,
      text: `Sample size is ${n} member${n !== 1 ? 's' : ''} — financial health insights are directional only.`,
      sentiment: 'warning',
    });
  }

  // Avg VeraScore
  if (fh.avg_verascore.value != null) {
    const score = Math.round(fh.avg_verascore.value * 10) / 10;
    const isStrong = score >= 60;
    bullets.push({
      icon: isStrong ? <FiTrendingUp size={14} /> : <FiAlertCircle size={14} />,
      text: `Portfolio average VeraScore is ${score}${isStrong ? ' — indicating healthy financial wellness' : ' — below the healthy threshold of 60'}.`,
      sentiment: isStrong ? 'positive' : 'negative',
    });
  }

  // At-risk bands
  const atRiskBands = fh.score_distribution.filter((b) => {
    const lower = parseInt(b.band.split('-')[0] ?? '0', 10);
    return lower < 40;
  });
  const atRiskCount = atRiskBands.reduce((s, b) => s + b.member_count, 0);
  const totalCount = fh.score_distribution.reduce((s, b) => s + b.member_count, 0);
  if (totalCount > 0 && atRiskCount > 0) {
    const pct = Math.round((atRiskCount / totalCount) * 100);
    bullets.push({
      icon: pct > 25 ? <FiAlertCircle size={14} /> : <FiMinus size={14} />,
      text: `${pct}% of members (${atRiskCount}) are in At Risk or Critical score bands.`,
      sentiment: pct > 25 ? 'negative' : 'neutral',
    });
  }

  // DTI factor
  if (fh.factor_breakdown.dti.value != null && n >= SUFFICIENCY_THRESHOLDS.STANDARD_MIN) {
    const dti = Math.round(fh.factor_breakdown.dti.value * 100) / 100;
    bullets.push({
      icon: dti > 40 ? <FiAlertCircle size={14} /> : <FiTrendingUp size={14} />,
      text: `Average DTI ratio is ${dti}%${dti > 40 ? ' — elevated debt burden across portfolio' : ' — within healthy range'}.`,
      sentiment: dti > 40 ? 'negative' : 'positive',
    });
  }

  return bullets.slice(0, 4);
}

// ── Panel Component ─────────────────────────────────────────────────────────

interface ExecutiveSummaryPanelProps {
  tab: 'overview' | 'financial_health';
  overview?: OverviewSection;
  financialHealth?: FinancialHealthSection;
}

const ExecutiveSummaryPanel: React.FC<ExecutiveSummaryPanelProps> = ({
  tab,
  overview,
  financialHealth,
}) => {
  let bullets: InsightBullet[] = [];

  if (tab === 'overview' && overview) {
    bullets = generateOverviewInsights(overview);
  } else if (tab === 'financial_health' && financialHealth) {
    bullets = generateFinancialHealthInsights(financialHealth);
  }

  if (bullets.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 sm:p-5">
      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <span className="bg-slate-100 p-1 rounded">📊</span>
        Executive Summary
      </h4>
      <div className="space-y-2">
        {bullets.map((bullet, i) => (
          <div
            key={i}
            className={`flex items-start gap-2.5 px-3 py-2 rounded-md border text-sm ${sentimentColors[bullet.sentiment]}`}
          >
            <span className="mt-0.5 flex-shrink-0">{bullet.icon}</span>
            <span>{bullet.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExecutiveSummaryPanel;
